import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Users,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Shield,
  CreditCard,
  HelpCircle,
  Building2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Pricing - Radius",
  "description": "Simple, transparent pricing for Radius B2B procurement platform. Free for buyers, success-based pricing for vendors.",
  "url": "https://useradius.io/pricing"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is Radius really free for buyers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! Buyers can post projects, receive quotes, compare bids, and manage their procurement process at no cost. You only pay for the work you approve with your chosen vendor."
      }
    },
    {
      "@type": "Question",
      "name": "How does vendor pricing work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Vendors only pay when they win work through the platform. There are no subscription fees or costs to join. Detailed pricing information is provided during the vendor onboarding process."
      }
    },
    {
      "@type": "Question",
      "name": "Are there any hidden fees?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No hidden fees. For buyers, the platform is completely free. For vendors, pricing is transparent and success-based—you only pay when you successfully win and complete work."
      }
    }
  ]
};

const clientBenefits = [
  "Post projects at no cost",
  "Receive competitive quotes from verified vendors",
  "Compare bids side-by-side",
  "Pay only for the work you approve",
  "All messages and files in one place",
  "Track project status from quote to delivery",
  "Funds protected with purchase protection",
  "No subscription required",
];

const organizationBenefits = [
  "Invite unlimited team members",
  "Collaborate on projects across your organization",
  "Set vendor eligibility requirements",
  "Organization-wide spend reporting",
  "Role-based access controls",
  "Centralized project and payment history",
  "Everything in the Buyers plan included",
];

const vendorBenefits = [
  "Free to join and create your profile",
  "Browse and respond to project opportunities",
  "Only pay when you win work",
  "Competitive platform fees",
  "Get paid faster with streamlined approvals",
  "Secure, reliable payment processing",
];

const faqs = [
  {
    question: "Is Radius really free for buyers?",
    answer: "Yes! Buyers can post projects, receive quotes, compare bids, and manage their procurement process at no cost. You only pay for the work you approve with your chosen vendor."
  },
  {
    question: "What's included in the Organizations plan?",
    answer: "The Organizations plan includes everything in the Buyers plan plus team collaboration, vendor eligibility controls, RFQ configuration, and organization-wide spend analytics. It's currently free for early adopters."
  },
  {
    question: "How does vendor pricing work?",
    answer: "Vendors only pay when they win work through the platform. There are no subscription fees or costs to join. Detailed pricing information is provided during the vendor onboarding process."
  },
  {
    question: "Are there any hidden fees?",
    answer: "No hidden fees. For buyers, the platform is completely free. For vendors, pricing is transparent and success-based—you only pay when you successfully win and complete work."
  },
  {
    question: "How do payments work?",
    answer: "All payments are processed securely through Stripe. Buyers can pay via credit card & ACH, or receive an invoice for their AP team to process. Funds are held securely and released to vendors based on project completion and delivery verification."
  },
  {
    question: "Can I try Radius before committing?",
    answer: "Absolutely. Buyers can start posting projects immediately at no cost. Vendors can create their profile and browse opportunities for free—you only pay when you win work."
  },
];

export default function Pricing() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Pricing - Simple & Transparent | Radius"
        description="Free for buyers, success-based pricing for vendors. No hidden fees, no surprises. Join the B2B procurement platform built to help you succeed."
        canonical="/pricing"
        structuredData={[pricingSchema, faqSchema]}
      />
      <PublicHeader />

      <main>
        {/* Hero Section */}
        <section className="container py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center space-y-6">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-muted-foreground">
              No hidden fees. No surprises. Just a platform built to help you succeed.
            </p>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="container pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* For Buyers Card */}
            <div className="bg-card border rounded-2xl p-8 shadow-lg flex flex-col">
              <div className="h-[152px]">
                <div className="flex items-center gap-3 mb-2 pt-2">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Users className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">For Solo Buyers</h2>
                </div>
                <p className="text-muted-foreground text-sm mb-6">
                  Source marketing materials with confidence
                </p>
              </div>
              
              <div className="mb-6 h-20">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-primary">Free</span>
                </div>
                <p className="text-muted-foreground mt-2">
                  No platform fees, ever
                </p>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {clientBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <Button asChild size="lg" variant="outline" className="w-full">
                  <Link to="/auth/signup?type=buyer">
                    Start Your First Project
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>

                <p className="text-sm text-muted-foreground text-center mt-4">
                  No credit card required
                </p>
              </div>
            </div>

            {/* For Organizations Card */}
            <div className="relative bg-card border-2 border-primary rounded-2xl p-8 shadow-lg flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                  Early Adopter
                </span>
              </div>
              <div className="h-[152px]">
                <div className="flex items-center gap-3 mb-2 pt-2">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Building2 className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">For Organizations</h2>
                </div>
                <p className="text-muted-foreground text-sm mb-6">
                  Procurement visibility and control for your team
                </p>
              </div>
              
              <div className="mb-6 h-20">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-primary">Free</span>
                  <span className="text-sm text-muted-foreground line-through">$299/mo</span>
                </div>
                <p className="text-muted-foreground mt-2">
                  Free for a limited time
                </p>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {organizationBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <Button asChild size="lg" className="w-full">
                  <Link to="/auth/signup?type=buyer">
                    Get Started Free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>

                <p className="text-sm text-muted-foreground text-center mt-4">
                  Lock in free access as an early adopter
                </p>
              </div>
            </div>

            {/* For Vendors Card */}
            <div className="bg-card border rounded-2xl p-8 shadow-lg flex flex-col">
              <div className="h-[152px]">
                <div className="flex items-center gap-3 mb-2 pt-2">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <TrendingUp className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">For Vendors</h2>
                </div>
                <p className="text-muted-foreground text-sm mb-6">
                  Win new business without upfront risk
                </p>
              </div>
              
              <div className="mb-6 h-20">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">Success-Based</span>
                </div>
                <p className="text-muted-foreground mt-2">
                  Only pay when you win work
                </p>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {vendorBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <Button asChild size="lg" variant="outline" className="w-full">
                  <Link to="/auth/signup?type=vendor">
                    Apply to Join as Vendor
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>

                <p className="text-sm text-muted-foreground text-center mt-4">
                  Detailed pricing provided during onboarding
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="border-y bg-muted/50 py-16">
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold">Secure Payments</h3>
                <p className="text-sm text-muted-foreground">
                  Stripe-powered with purchase protection
                </p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold">Vendor Profiles</h3>
                <p className="text-sm text-muted-foreground">
                  Documented capabilities and platform history
                </p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold">Purchase Protection</h3>
                <p className="text-sm text-muted-foreground">
                  Funds protected until work is delivered and approved
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container py-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <div className="flex justify-center">
                <div className="p-3 rounded-xl bg-primary/10">
                  <HelpCircle className="h-7 w-7 text-primary" />
                </div>
              </div>
              <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Everything you need to know about our pricing
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section className="border-t bg-primary/5 py-20">
          <div className="container text-center space-y-6">
            <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Join the platform that's simplifying B2B procurement for buyers and vendors alike.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/auth/signup?type=buyer">
                  Start as Buyer — Free
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/auth/signup?type=vendor">
                  Apply as Vendor
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
